import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ============================
   TAX RATES — READ ONLY

   This slice used to carry create, update, toggle and delete as well. All four
   pointed at Node's /api/taxmaster, and none of them could be repointed,
   because what they edited no longer exists: there is no TaxMaster table.

   A rate now comes from one of two places. A `legacy` rate is the percentage
   held on the product itself and is changed on the product form. A dated rate
   is a row in catalog.CfgTaxRates, in force between its own validFrom and
   validTo, and superseded by a new row starting when the old one ends — never
   switched off, which is why there was nothing a status toggle could flip.

   The ids this list renders are synthetic keys such as `legacy:0.00:NOTAX`,
   and no write route accepts one. So the four writes are gone rather than
   left pointing at a retired path: an exported thunk is an invitation, and the
   next screen to import one would fail exactly as the last did.
============================ */

const apiError = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* The route answers every rate in one shot. The page/limit/search that callers
   used to pass are not accepted and not sent — filtering a dozen rows is the
   screen's job, and a server-side pager that hides the rate someone is looking
   for is worse than none. */
export const fetchTaxMasters = createAsyncThunk(
  "taxMaster/fetchAll",
  async (_args, thunkAPI) => {
    try {
      const res = await api.get("/admin/tax-masters");
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        apiError(err, "Could not load the tax rates.")
      );
    }
  }
);

const taxMasterSlice = createSlice({
  name: "taxMaster",
  initialState: {
    taxMasters: [],
    loading: false,
    error: null,
    pagination: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaxMasters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaxMasters.fulfilled, (state, action) => {
        state.loading = false;
        state.taxMasters = action.payload.taxMasters || [];
        /* The route reports no pages, so the pager stays empty rather than
           being invented from the count. */
        state.pagination = {};
      })
      .addCase(fetchTaxMasters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default taxMasterSlice.reducer;
